import {
  Component,
  OnDestroy,
  ViewChild,
  input,
  signal,
  inject,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { Export } from 'depoto-core/src/entities'
import { CoreService } from '../../services'
import { ModalContainerComponent } from '../../components/_common/modal-container/modal-container.component'
import { exportsColumns } from '../../utils/definitions.util'
import { ExportService } from '../../services/entity-services/export.service'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { SchemaUtil } from '../../utils'

@Component({
  selector: 'app-exports',
  templateUrl: 'exports.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExportsComponent extends BaseListing implements OnDestroy {
  private exportsService = inject(ExportService)

  @ViewChild('modal') childModal: ModalContainerComponent

  entity = input.required<string>()

  headerTitle: string
  listingColumns = signal(exportsColumns)

  sortProp = signal('created')
  sortDir = signal('desc')

  selectedType = signal<string>('')

  readonly queryFilters = computed(() => {
    const filters: any = { group: this.entity() }
    if (this.selectedType()?.length && this.selectedType() !== 'all' && this.selectedType() !== 'none') {
      filters.types = [this.selectedType()]
    }
    return filters
  })

  readonly queryTypesOptions = injectQuery(() => ({
    queryKey: ['types', 'options', this.entity()],
    queryFn: async () => {
      return await this.exportsService
        .getTypes({}, { id: null, name: null, format: null, group: null })
        .then(result => {
          // todo exportTypes.filter(t => t.group === this.entity())
          return result.items?.filter(t => t.group === this.entity())
        })
    },
    select: data => {
      if ((data as any)?.length === 1) {
        this.selectedType.set(data[0].id)
        return data
      } else {
        return [{ id: 'all', name: 'all' }, ...(data as any)]
      }
    },
  }))

  readonly queryExports = injectInfiniteQuery(() => ({
    queryKey: [
      'exports',
      {
        entity: this.entity(),
        sortProp: this.sortProp(),
        sortDir: this.sortDir(),
        filters: this.queryFilters(),
      },
    ],
    queryFn: async ({ pageParam }) => {
      return await this.exportsService
        .getExports(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          {
            id: null,
            file: SchemaUtil.file,
            generated: null,
            created: null,
            url: null,
            type: { id: null, name: null, format: null, group: null },
            filter: null,
            createdBy: { id: null, name: null },
            note: null,
          }
        )
        .then(result => {
          this.saveViewSettings('exports', {
            ...this.loadViewSettings('exports'),
            [this.entity() + '_type']: this.selectedType(),
          })

          if (!this.childModal?.childModal?.isShown) {
            this.stopRefreshing()
            return result
          }

          const nonGeneratedItems = (result.items ?? []).filter(item => !item?.generated)
          if (!nonGeneratedItems.length) {
            this.stopRefreshing()
          }

          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
    enabled: false,
  }))

  totalItems = computed(() => this.queryExports.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      // const types = this.queryTypesOptions.data()
      // if (types?.length === 1) {
      //   this.selectedType.set(types[0].id)
      // }
      this.queryExports.data()
    })
  }

  ngOnDestroy() {
    this.stopRefreshing()
  }

  selectedTypeChanged(event) {
    this.selectedType.set(event)
    this.queryExports.refetch()
  }

  download(exportObj: Export) {
    this.core.services.download.downloadDocumentAndShowInNewWindow(exportObj.url)
  }

  private setDefaultSelectedType(types = this.queryTypesOptions.data()): void {
    const availableTypes = ((types as any[]) ?? []).filter(type => type?.id && type.id !== 'all')
    if (!this.selectedType()?.length && availableTypes.length === 1) {
      this.selectedType.set(availableTypes[0].id)
    }
  }

  async showChildModal(): Promise<void> {
    this.loadSorting('exports')

    // this.selectedType.set(this.loadViewSettings('exports')?.[this.entity() + '_type'] || '')
    this.headerTitle = `exports.${this.entity()}`

    this.childModal.showChildModal()
    const typesResult = await this.queryTypesOptions.refetch()
    this.setDefaultSelectedType((typesResult as any)?.data)
    this.startRefreshing('queryExports')
    await this.queryExports.refetch()
  }

  onHideChildModal(): void {
    this.stopRefreshing()
  }
}
